import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {catchError, map, retry, shareReplay} from "rxjs/operators";
import {GroupedStore, Store} from "../models";
import {AuthService} from "./auth.service";
import {BehaviorSubject, Observable, of} from 'rxjs';
import {multiStoresMock} from "../mocks/multi_stores";
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  private storesSubject = new BehaviorSubject<Store[]>([]);
  private groupedStoresByZoneSubjects: Map<string | undefined, BehaviorSubject<GroupedStore | undefined>> = new Map();
  private loadedZones: Set<string | undefined> = new Set();
  private storesLoaded = false;

  private resource: string = `${environment.wallet_api_url}${environment.v1}/Inventory/Stores`;  // api/v1
  private resourceGS: string = `${environment.wallet_api_url}${environment.v1}/Inventory/Stores/ByZone`;  // api/v1

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
  ) {
  }

  getStores(): Observable<Store[]> {
    if (this.storesLoaded) {
      return this.storesSubject.asObservable();
    }

    return this.httpClient.get<any>(`${this.resource}`, {
      params: new HttpParams(),
      headers: this.getHeader(),
    }).pipe(
      map((response: {fails: boolean, data: any[]}): Store[] => {
        if(response.fails) {
          return []
        }
        const stores = response.data;
        const mappedStores = stores.map((store: any) => {
          return {
            id: store.id,
            name: store.name,
            logoUrl: store.logoUrl,
            metaKeywords: store.metaKeywords?.split(', ').slice(0, 3),
            code: store.code,
            description: store.description,
            favorite_color: store.favoriteColor
          };
        });
        // Update the behavior subject with new data
        this.storesLoaded = true;
        this.storesSubject.next(mappedStores);
        return mappedStores;
      }),
      catchError((err: any): Observable<Store[]> => {
        const fallbackStores = multiStoresMock.map((store: any) => {
          return {
            id: store.id,
            name: store.name,
            logoUrl: store.logoUrl,
            metaKeywords: store.metaKeywords?.split(', ').slice(0, 3),
            code: store.code,
            description: store.description,
            favorite_color: store.favoriteColor
          };
        });
        // Update the behavior subject with fallback data
        this.storesLoaded = true;
        this.storesSubject.next(fallbackStores);
        return of(fallbackStores);
      }),
      shareReplay(1)
    );
  }

  getGroupedStores() {
    return this.httpClient.get<any>(`${this.resourceGS}`, {
      params: new HttpParams(),
      headers: this.getHeader(),
    }).pipe(
      map((response: {fails: boolean, data: any}): GroupedStore[] => {
        if(response.fails) {
          return []
        }

        return response.data.groupedStores.map((groupedStore: any) => {
          const gs: GroupedStore = {
            zoneId: groupedStore.zoneId,
            zoneName: groupedStore.zoneName,
            stores: groupedStore.stores.map((store: any) => {
              return {
                id: store.id,
                name: store.name,
                logoUrl: store.logoUrl,
                metaKeywords: store.metaKeywords?.split(', ').slice(0, 3),
                code: store.code,
                favorite_color: store.favoriteColor
              };
            }),
          };

          // Update BehaviorSubject for this zone if it exists
          this.loadedZones.add(groupedStore.zoneId);
          if (!this.groupedStoresByZoneSubjects.has(groupedStore.zoneId)) {
            this.groupedStoresByZoneSubjects.set(groupedStore.zoneId, new BehaviorSubject<GroupedStore | undefined>(gs));
          } else {
            this.groupedStoresByZoneSubjects.get(groupedStore.zoneId)!.next(gs);
          }

          return gs;
        });
      }),
      retry(environment.retry_on_fail),
      shareReplay(1)
    );
  }

  getStoreById(storeId: string) {
    return this.storesSubject.pipe(
      map(stores => {
        return stores.find(store => store.id === storeId);
      })
    );
  }

  // Add a private property to track in-flight requests
  private inFlightRequests: Map<string | undefined, Observable<GroupedStore | undefined>> = new Map();

  // TODO: This method is ultra complicated because its being fetched in several components and
  //  I need to cache the response so it doesnt repeat itself. We need to simplify it
  getStoresByZoneId(zoneId?: string): Observable<GroupedStore | undefined> {
    // Return from cache if already loaded
    if (this.loadedZones.has(zoneId)) {
      return this.groupedStoresByZoneSubjects.get(zoneId)!.asObservable();
    }

    // Return the in-flight request if one is already in progress
    if (this.inFlightRequests.has(zoneId)) {
      return this.inFlightRequests.get(zoneId)!;
    }

    // Create a subject for this zoneId if it doesn't exist
    if (!this.groupedStoresByZoneSubjects.has(zoneId)) {
      this.groupedStoresByZoneSubjects.set(zoneId, new BehaviorSubject<GroupedStore | undefined>(undefined));
    }

    // Create the request observable
    const request = this.httpClient.get<any>(`${this.resourceGS}/${zoneId}`, {
      params: new HttpParams(),
      headers: this.getHeader(),
    }).pipe(
      map((response: {fails: boolean, data: any}): GroupedStore | undefined => {
        if (response.fails) {
          throw new Error(`Error fetching stores for zone ${zoneId}`);
        }

        const groupedStore = response.data;
        if (!groupedStore) return undefined;

        const gs: GroupedStore = {
          zoneId: groupedStore.zoneId,
          zoneName: groupedStore.zoneName,
          stores: groupedStore.stores.map((store: any) => ({
            id: store.id,
            name: store.name,
            logoUrl: store.logoUrl,
            metaKeywords: store.metaKeywords?.split(', ').slice(0, 3),
            code: store.code,
            favorite_color: store.favoriteColor
          }))
        };

        // Update behavior subject
        this.loadedZones.add(zoneId);
        this.groupedStoresByZoneSubjects.get(zoneId)!.next(gs);

        // Remove from in-flight requests
        this.inFlightRequests.delete(zoneId);

        return gs;
      }),
      retry(environment.retry_on_fail),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    // Store the request
    this.inFlightRequests.set(zoneId, request);

    return request;
  }

  getStoreByCode(storeCode: string): Observable<Store | undefined> {
    if (this.storesLoaded) {
      return this.storesSubject.pipe(
        map(stores => stores.find(store => store.code === storeCode))
      );
    }

    // Otherwise, load the stores first and then find the store by code
    return this.getStores().pipe(
      map(stores => {
        return stores.find(store => store.code === storeCode)
      })
    );
  }

  /**
   * Get headers
   * @private
   */
  private getHeader(): HttpHeaders {
    return this.authService.getHeaderNoRefresh();
  }

}
